import { useEffect, useState } from "react";
import { Handshake, PanelLeftClose, PanelLeftOpen, Sparkles, ChevronRight, ShieldCheck, Crown } from "lucide-react";
import { TripleParlayBuilder } from "./TripleParlayBuilder";
import { TopPicksBoard } from "./TopPicksBoard";
import { ConfidenceParlay } from "./ConfidenceParlay";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";

const useIsDesktop = () => {
  const q = "(min-width: 1024px)";
  const [is, setIs] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const fn = (e) => setIs(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return is;
};

const ConsensusEntry = ({ onOpen }) => (
  <button
    data-testid="open-consensus-builder"
    onClick={onOpen}
    className="group w-full rounded-2xl border border-[#10B981]/40 bg-gradient-to-br from-[#10B981]/15 to-[#00E5FF]/10 p-4 text-left hover:brightness-110 transition-[filter]"
  >
    <span className="flex items-center gap-3">
      <span className="grid place-items-center h-9 w-9 rounded-xl bg-[#10B981]/20 text-[#10B981] shrink-0">
        <Handshake className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-heading font-bold text-white text-sm">Cross-Sport Consensus Parlay</span>
        <span className="block text-[11px] text-slate-400">Mix props from games you've opened — AI + Market agreement</span>
      </span>
      <ChevronRight className="h-4 w-4 text-[#10B981] shrink-0 transition-transform group-hover:translate-x-0.5" />
    </span>
  </button>
);

const PanelBody = ({ sport, source, hasGames, onOpenBuilder }) => {
  const [triple, setTriple] = useState(false);
  const [starters, setStarters] = useState(false);
  return (
  <div className="space-y-4" data-testid="ai-tools-body">
    <ConsensusEntry onOpen={onOpenBuilder} />
    <button
      data-testid="open-triple-parlay"
      onClick={() => setTriple(true)}
      className="group w-full rounded-2xl border border-[#A78BFA]/40 bg-gradient-to-br from-[#A78BFA]/15 to-[#00E5FF]/10 p-4 text-left hover:brightness-110 transition-[filter]"
    >
      <span className="flex items-center gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-[#A78BFA]/20 text-[#A78BFA] shrink-0">
          <ShieldCheck className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-heading font-bold text-white text-sm">Triple-Check Parlay</span>
          <span className="block text-[11px] text-slate-400">AI projection + most sportsbooks + AI trend read, from games you've opened</span>
        </span>
        <ChevronRight className="h-4 w-4 text-[#A78BFA] shrink-0 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
    <TripleParlayBuilder open={triple} onClose={() => setTriple(false)} scopeLabel="all games you've opened" />
    <button
      data-testid="open-starters-parlay"
      onClick={() => setStarters(true)}
      className="group w-full rounded-2xl border border-[#F59E0B]/40 bg-gradient-to-br from-[#F59E0B]/15 to-[#3B82F6]/10 p-4 text-left hover:brightness-110 transition-[filter]"
    >
      <span className="flex items-center gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-[#F59E0B]/20 text-[#F59E0B] shrink-0">
          <Crown className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-heading font-bold text-white text-sm">Starters-First Parlay</span>
          <span className="block text-[11px] text-slate-400">Triple-Check legs ordered starters first, then bench, with superstars starred</span>
        </span>
        <ChevronRight className="h-4 w-4 text-[#F59E0B] shrink-0 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
    <TripleParlayBuilder mode="starters" open={starters} onClose={() => setStarters(false)} scopeLabel="all games you've opened" />
    {hasGames ? (
      <>
        <TopPicksBoard key={`${sport}-${source}`} sport={sport} source={source} />
        <ConfidenceParlay key={`parlay-${sport}-${source}`} sport={sport} source={source} />
      </>
    ) : (
      <p className="text-xs text-slate-500 px-1">Best Options and Confidence Parlay appear once this sport has games loaded.</p>
    )}
  </div>
  );
};

const PanelTitle = () => (
  <span className="flex items-center gap-2">
    <span className="grid place-items-center h-7 w-7 rounded-lg bg-[#00E5FF]/15 text-[#00E5FF]">
      <Sparkles className="h-4 w-4" />
    </span>
    <span className="font-heading font-bold text-white text-sm tracking-wide">AI Betting Tools</span>
  </span>
);

export const AIToolsPanel = (props) => {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(() => localStorage.getItem("aiToolsOpen") !== "0");
  const [drawer, setDrawer] = useState(false);

  const toggle = () => {
    localStorage.setItem("aiToolsOpen", open ? "0" : "1");
    setOpen(!open);
  };

  if (!isDesktop) {
    return (
      <>
        <button
          data-testid="ai-tools-drawer-tab"
          onClick={() => setDrawer(true)}
          className="fixed left-0 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-1.5 rounded-r-xl border border-l-0 border-[#00E5FF]/40 bg-[#141C2B]/95 backdrop-blur px-1.5 py-3 text-[#00E5FF] shadow-lg"
        >
          <Sparkles className="h-4 w-4" />
          <span className="font-data text-[10px] font-bold tracking-widest [writing-mode:vertical-rl] rotate-180">AI TOOLS</span>
        </button>
        <Sheet open={drawer} onOpenChange={setDrawer}>
          <SheetContent side="left" data-testid="ai-tools-drawer"
            className="w-[92vw] max-w-[400px] bg-[#0B0F17] border-[#233248] p-4 overflow-y-auto">
            <SheetHeader className="mb-4 text-left">
              <SheetTitle><PanelTitle /></SheetTitle>
              <SheetDescription className="text-xs text-slate-500">Parlay builders and AI-ranked plays for the current sport.</SheetDescription>
            </SheetHeader>
            <PanelBody {...props} onOpenBuilder={() => { setDrawer(false); props.onOpenBuilder(); }} />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  if (!open) {
    return (
      <aside className="shrink-0 sticky top-20 self-start">
        <button
          data-testid="ai-tools-expand"
          onClick={toggle}
          className="flex flex-col items-center gap-2 rounded-xl border border-[#233248] bg-[#141C2B] px-2 py-4 text-[#00E5FF] hover:border-[#00E5FF]/50 transition-colors"
        >
          <PanelLeftOpen className="h-4 w-4" />
          <span className="font-data text-[10px] font-bold tracking-widest [writing-mode:vertical-rl] rotate-180">AI TOOLS</span>
        </button>
      </aside>
    );
  }

  return (
    <aside data-testid="ai-tools-panel"
      className="w-[380px] shrink-0 sticky top-20 self-start max-h-[calc(100vh-6rem)] overflow-y-auto no-scrollbar rounded-2xl border border-[#233248] bg-[#0E1420] p-4">
      <div className="flex items-center justify-between mb-4">
        <PanelTitle />
        <button data-testid="ai-tools-collapse" onClick={toggle} aria-label="Collapse AI tools"
          className="grid place-items-center h-8 w-8 rounded-lg text-slate-400 hover:text-white hover:bg-[#1B273E] transition-colors">
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>
      <PanelBody {...props} />
    </aside>
  );
};
